import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type {
  DefinizioneColonna,
  FiltroColonna,
  OperatoreFiltro,
  PeriodoCorrente,
  ValoreDistinto,
} from '../../lib/tipi';
import {
  ETICHETTE_OPERATORE,
  ETICHETTE_PERIODO,
  etichettaTipo,
  filtroCompleto,
  TIPI_TEMPORALI,
  valoriAttesi,
} from '../../lib/tipi';

interface Props {
  colonna: DefinizioneColonna;
  /** Il bottone d'intestazione sotto cui il menu si apre: da li' escono le coordinate. */
  ancora: HTMLElement;
  filtroAttivo?: FiltroColonna;
  caricaValori: () => Promise<ValoreDistinto[]>;
  onApplica: (filtro: FiltroColonna | null) => void;
  onOrdina: (discendente: boolean) => void;
  onChiudi: () => void;
}

const PERIODI: PeriodoCorrente[] = [
  'Oggi',
  'Ieri',
  'SettimanaCorrente',
  'MeseCorrente',
  'TrimestreCorrente',
  'AnnoCorrente',
];

/**
 * Il menu di colonna. Gli operatori mostrati vengono dal tipo di dato dichiarato
 * dal backend: è questo che rende il filtro consapevole di cosa contiene la colonna.
 */
export function MenuFiltro({
  colonna,
  ancora,
  filtroAttivo,
  caricaValori,
  onApplica,
  onOrdina,
  onChiudi,
}: Props) {
  const [operatore, setOperatore] = useState<OperatoreFiltro>(
    filtroAttivo?.operatore ?? colonna.operatori[0]!,
  );
  const [valori, setValori] = useState<string[]>(
    (filtroAttivo?.valori ?? []).map((v) => v ?? ''),
  );
  const [distinti, setDistinti] = useState<ValoreDistinto[] | null>(null);
  const [cercaValore, setCercaValore] = useState('');
  const elenco = operatore === 'EUnoDi' || operatore === 'NonEUnoDi';

  useEffect(() => {
    if (!elenco || distinti !== null) return;
    let annullato = false;
    caricaValori().then((v) => {
      if (!annullato) setDistinti(v);
    });
    return () => {
      annullato = true;
    };
  }, [elenco, distinti, caricaValori]);

  function aggiorna(indice: number, valore: string) {
    setValori((precedenti) => {
      const copia = [...precedenti];
      copia[indice] = valore;
      return copia;
    });
  }

  function commuta(valore: string) {
    setValori((precedenti) =>
      precedenti.includes(valore)
        ? precedenti.filter((v) => v !== valore)
        : [...precedenti, valore],
    );
  }

  function applica() {
    const filtro: FiltroColonna = { colonna: colonna.nome, operatore, valori };
    if (!filtroCompleto(filtro)) return;
    onApplica(filtro);
  }

  const attesi = valoriAttesi(operatore);
  /*  Il selettore di data anche su una colonna che porta l'ora: il filtro ragiona per
      giornate, e un campo datetime-local chiederebbe un'ora che poi verrebbe buttata via.  */
  const tipoInput = TIPI_TEMPORALI.includes(colonna.tipo)
    ? 'date'
    : colonna.tipo === 'Testo'
      ? 'text'
      : 'number';

  /*  IL MENU ESCE DALLA TABELLA. Segnalato da Andrea il 15/09: «le finestre per le
      ricerche tra le colonne non si vedono e vengono troncate».

      Il motivo: il menu stava dentro il <th>, e il <th> sta dentro `.tabella-contenitore`,
      che ha `overflow-x: auto` per far scorrere le colonne. Una volta che un antenato
      ritaglia, ritaglia tutto quello che ha dentro — e l'elenco dei valori, che e' la
      parte utile, e' proprio quella che finiva fuori dal bordo inferiore.

      Non si risolve con lo z-index: quello decide chi sta davanti a chi, non chi viene
      tagliato. L'unico modo e' uscire da quel contenitore, quindi il menu si disegna in
      fondo alla pagina (`createPortal`) e si posiziona a mano sotto la sua intestazione.
      Il prezzo e' questo blocco di codice; il guadagno e' un menu che si vede sempre,
      anche sull'ultima colonna di una tabella che scorre.  */
  const riquadro = useRef<HTMLDivElement>(null);
  const [posizione, setPosizione] = useState<{ top: number; left: number; maxHeight: number }>();

  useLayoutEffect(() => {
    function colloca() {
      const a = ancora.getBoundingClientRect();
      const larghezza = riquadro.current?.offsetWidth ?? 250;
      const altezza = riquadro.current?.offsetHeight ?? 320;
      const margine = 8;

      /*  Allineato al bordo dell'intestazione, ma senza uscire dallo schermo: sull'ultima
          colonna a destra il menu si sposta dentro invece di finire fuori.  */
      const left = Math.min(
        Math.max(margine, a.left),
        window.innerWidth - larghezza - margine,
      );

      /*  Sotto se ci sta, sopra se sotto non ci sta, e in ogni caso mai piu' alto dello
          spazio che ha: quello che avanza lo scorre il corpo del menu. Un menu che esce
          dallo schermo dal basso e' lo stesso difetto di prima, spostato.  */
      const sotto = window.innerHeight - a.bottom - margine * 2;
      const sopra = a.top - margine * 2;
      const stiaSopra = altezza > sotto && sopra > sotto;

      setPosizione({
        top: stiaSopra ? Math.max(margine, a.top - Math.min(altezza, sopra) - 4) : a.bottom + 4,
        left,
        maxHeight: Math.max(160, stiaSopra ? sopra : sotto),
      });
    }

    colloca();

    /*  Se la pagina scorre o la finestra cambia misura, il menu deve seguire la sua
        intestazione: `true` sul terzo argomento perche' a scorrere e' il contenitore
        della tabella, non la finestra, e gli eventi di scorrimento non risalgono.  */
    window.addEventListener('resize', colloca);
    window.addEventListener('scroll', colloca, true);
    return () => {
      window.removeEventListener('resize', colloca);
      window.removeEventListener('scroll', colloca, true);
    };
  }, [ancora]);

  /*  Fuori dal menu si chiude. Serviva anche prima, ma da quando il menu non e' piu'
      dentro il <th> serve per forza: un clic altrove non lo sfiora nemmeno.  */
  useEffect(() => {
    function fuori(e: MouseEvent) {
      const bersaglio = e.target as Node;
      if (riquadro.current?.contains(bersaglio) || ancora.contains(bersaglio)) return;
      onChiudi();
    }
    function tasto(e: KeyboardEvent) {
      if (e.key === 'Escape') onChiudi();
    }
    document.addEventListener('mousedown', fuori);
    document.addEventListener('keydown', tasto);
    return () => {
      document.removeEventListener('mousedown', fuori);
      document.removeEventListener('keydown', tasto);
    };
  }, [ancora, onChiudi]);

  return createPortal(
    <div
      className="menu-filtro"
      role="dialog"
      aria-label={`Filtro su ${colonna.nome}`}
      ref={riquadro}
      style={
        posizione === undefined
          ? /*  Prima di sapere dove sta, si disegna fuori vista invece che in alto a
                sinistra: misurarlo serve, vederlo saltare no.  */
            { visibility: 'hidden' }
          : { top: posizione.top, left: posizione.left, maxHeight: posizione.maxHeight }
      }
    >
      <header>
        <strong>{colonna.nome}</strong>
        <span className="tipo">{etichettaTipo(colonna.tipo)}</span>
      </header>

      <div className="corpo">
        <div className="operatori">
          {colonna.operatori.map((op) => (
            <label key={op} className={op === operatore ? 'on' : ''}>
              <input
                type="radio"
                name={`op-${colonna.nome}`}
                checked={op === operatore}
                onChange={() => {
                  setOperatore(op);
                  setValori([]);
                }}
              />
              {ETICHETTE_OPERATORE[op]}
            </label>
          ))}
        </div>

        {operatore === 'PeriodoCorrente' && (
          <div className="scorciatoie">
            {PERIODI.map((p) => (
              <button
                key={p}
                type="button"
                className={valori[0] === p ? 'on' : ''}
                onClick={() => setValori([p])}
              >
                {ETICHETTE_PERIODO[p]}
              </button>
            ))}
          </div>
        )}

        {elenco && (
          <>
            <input
              className="campo"
              placeholder="Cerca nei valori…"
              value={cercaValore}
              onChange={(e) => setCercaValore(e.target.value)}
            />
            <div className="checklist">
              {distinti === null && <p className="attesa">Carico i valori…</p>}
              {distinti
                ?.filter((v) =>
                  (v.valore ?? '').toLowerCase().includes(cercaValore.toLowerCase()),
                )
                .map((v) => {
                  const chiave = v.valore ?? '';
                  return (
                    <label key={chiave || '(vuoto)'}>
                      <input
                        type="checkbox"
                        checked={valori.includes(chiave)}
                        onChange={() => commuta(chiave)}
                      />
                      <span>{v.valore ?? '(vuoto)'}</span>
                      {/* Senza il conteggio la checklist non dice nulla su cosa filtrerà. */}
                      <em>{v.occorrenze}</em>
                    </label>
                  );
                })}
            </div>
          </>
        )}

        {!elenco && operatore !== 'PeriodoCorrente' && attesi > 0 && (
          <div className="valori">
            {Array.from({ length: attesi }, (_, i) => (
              <input
                key={i}
                className="campo"
                type={tipoInput}
                inputMode={tipoInput === 'number' ? 'decimal' : undefined}
                placeholder={attesi === 2 ? (i === 0 ? 'da' : 'a') : undefined}
                value={valori[i] ?? ''}
                onChange={(e) => aggiorna(i, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applica();
                }}
              />
            ))}
          </div>
        )}
      </div>

      {colonna.ordinabile && (
        <div className="ordina">
          <button type="button" onClick={() => onOrdina(false)}>
            {etichettaOrdine(colonna, false)}
          </button>
          <button type="button" onClick={() => onOrdina(true)}>
            {etichettaOrdine(colonna, true)}
          </button>
        </div>
      )}

      <footer>
        <button type="button" className="chiaro" onClick={() => onApplica(null)}>
          Rimuovi
        </button>
        <div className="destra">
          <button type="button" className="chiaro" onClick={onChiudi}>
            Annulla
          </button>
          <button type="button" className="primario" onClick={applica}>
            Applica
          </button>
        </div>
      </footer>
    </div>,
    document.body,
  );
}

/** Anche l'etichetta dell'ordinamento dipende dal tipo: "A→Z" su un importo non significa niente. */
function etichettaOrdine(colonna: DefinizioneColonna, discendente: boolean): string {
  switch (colonna.tipo) {
    case 'Data':
    case 'DataOra':
      return discendente ? 'Recenti prima' : 'Vecchie prima';
    case 'Numero':
    case 'Importo':
    case 'Percentuale':
      return discendente ? '9 → 1' : '1 → 9';
    default:
      return discendente ? 'Z → A' : 'A → Z';
  }
}
